import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  FileCheck2, AlertTriangle, ShieldCheck, ClipboardList, Flame, Pickaxe,
  HardHat, Users, ChevronRight, Clock, Shield, Zap,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Card, Pill, EmptyState, Spinner } from "@/components/ui";
import { fmtDateTime } from "@/lib/dates";

// =============================================================================
// Forms screen — starting point for any form submission
// =============================================================================
// Groups active form_templates by kind. Each tile opens /forms/run/<code>
// which loads the runtime.
//
// 6 Oct 2026 — James: "Why only hot works in permits in forms?" Because only
// a hot-work FORM TEMPLATE was ever seeded; the real permits (all six types,
// numbered, approved, tracked) live in the Permits module at /permits. So the
// Permits group here is no longer "whatever templates exist" — it is the six
// permit types, each opening the Permits module's issue screen, and any
// legacy permit_* form template is hidden so there is one way to raise one.
// =============================================================================

// The six permit types the Permits module issues (same vocabulary as
// features/permits/PermitsScreen.tsx and the permit_type enum).
const PERMIT_TYPES: { key: string; label: string; icon: any; hint: string }[] = [
  { key: "hot_work",           label: "Hot Work",           icon: Flame,   hint: "Welding, grinding, anything with ignition." },
  { key: "excavation",         label: "Excavation",         icon: Pickaxe, hint: "Any ground disturbance — DBYD required." },
  { key: "workbox",            label: "Workbox / EWP",      icon: HardHat, hint: "EWP or workbox-suspended works." },
  { key: "confined_space",     label: "Confined Space",     icon: Shield,  hint: "Entry to pits, tanks, ducts." },
  { key: "working_at_heights", label: "Working at Heights", icon: HardHat, hint: "Where fall protection is engaged." },
  { key: "live_electrical",    label: "Live Electrical",    icon: Zap,     hint: "Testing, isolation, or live energised work." },
];

const KIND_META: Record<string, { label: string; icon: any; group: string; hint: string }> = {
  take5:            { label: "Take 5",              icon: ShieldCheck,  group: "HSE",     hint: "Quick risk check before starting a task." },
  prestart:         { label: "Prestart",            icon: ClipboardList, group: "HSE",    hint: "Start-of-shift site check." },
  jsea:             { label: "Prestart / JSEA",     icon: ClipboardList, group: "HSE",    hint: "Team briefing + multi-worker sign-on." },
  incident:         { label: "Incident Report",     icon: AlertTriangle, group: "HSE",    hint: "Any injury, near-miss, damage, or notifiable event." },
  near_miss:        { label: "Near Miss",           icon: AlertTriangle, group: "HSE",    hint: "Report something that could have gone wrong." },
  rehab:            { label: "Return to Work Plan", icon: Users,         group: "HR",     hint: "Rehabilitation plan for an injured worker." },
  induction:        { label: "Site Induction",      icon: Users,         group: "HSE",    hint: "Per-site acknowledgement of hazards + emergency." },
  permit_hot_work:     { label: "Permit · Hot Work",       icon: Flame,    group: "Permits", hint: "Welding, grinding, anything with ignition." },
  permit_excavation:   { label: "Permit · Excavation",     icon: Pickaxe,  group: "Permits", hint: "Any ground disturbance — DBYD required." },
  permit_workbox:      { label: "Permit · Workbox / EWP",  icon: HardHat,  group: "Permits", hint: "EWP or workbox-suspended works." },
  permit_confined:     { label: "Permit · Confined Space", icon: HardHat,  group: "Permits", hint: "Entry to pits, tanks, ducts." },
  permit_heights:      { label: "Permit · Heights",        icon: HardHat,  group: "Permits", hint: "Working from heights where fall protection is engaged." },
  permit_electrical:   { label: "Permit · Live Electrical", icon: Flame,   group: "Permits", hint: "Testing, isolation, or live energised work." },
  vehicle_inspection:  { label: "Vehicle Inspection",  icon: ClipboardList, group: "Assets",  hint: "Pre-drive vehicle check." },
  tool_checkout:       { label: "Tool Checkout",       icon: ClipboardList, group: "Assets",  hint: "Sign a tool out / back in." },
  custom:              { label: "Custom Form",         icon: FileCheck2,   group: "Other",    hint: "Custom form." },
};

export default function FormsScreen() {
  const user = useRequireAuth();

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["form_templates_available"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("form_templates")
        .select("template_id, template_code, display_name, form_kind, version")
        .eq("is_active", true)
        .order("template_code");
      if (error) throw error;
      // Keep the latest version per template_code
      const byCode: Record<string, any> = {};
      for (const t of data ?? []) {
        if (!byCode[t.template_code] || t.version > byCode[t.template_code].version) {
          byCode[t.template_code] = t;
        }
      }
      return Object.values(byCode);
    },
  });

  const { data: recent = [] } = useQuery({
    queryKey: ["my_form_submissions", user?.employee_id],
    enabled: !!user?.employee_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("form_submissions")
        .select(`
          submission_id, submission_number, captured_at, status,
          template:form_templates(display_name, template_code, form_kind),
          project:projects(project_code)
        `)
        .eq("submitted_by_employee_id", user!.employee_id!)
        .order("captured_at", { ascending: false })
        .limit(8);
      if (error) throw error;
      return data;
    },
  });

  // Group templates — permit_* templates are hidden: permits are raised in
  // the Permits module (see the Permits section below).
  const groups: Record<string, any[]> = {};
  for (const t of templates as any[]) {
    if (String(t.form_kind ?? "").startsWith("permit_")) continue;
    const meta = KIND_META[t.form_kind] ?? KIND_META.custom;
    (groups[meta.group] ||= []).push(t);
  }

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold tracking-tight">Forms</h1>
        <div className="text-text-muted text-[13px] mt-1">
          Pick a form. Each one's offline-first — if you're out of signal, hit submit anyway and it'll sync when you're back.
        </div>
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner size={18} /></div>
      ) : templates.length === 0 ? (
        <Card><EmptyState title="No forms available" description="Ask a director to activate form templates." icon={FileCheck2} /></Card>
      ) : (
        <div className="space-y-6 mb-8">
          {Object.entries(groups).map(([group, list]) => (
            <section key={group}>
              <h2 className="text-[11px] font-semibold uppercase tracking-wider2 text-text-muted mb-2">{group}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {list.map((t: any) => {
                  const meta = KIND_META[t.form_kind] ?? KIND_META.custom;
                  const Icon = meta.icon;
                  return (
                    <Link key={t.template_id} to={`/forms/run/${t.template_code}`}>
                      <Card className="h-full">
                        <div className="p-4 flex gap-3 items-start h-full">
                          <div className="w-9 h-9 rounded-md bg-accent/12 flex items-center justify-center shrink-0">
                            <Icon size={16} className="text-accent" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[14px] text-text font-medium">{t.display_name}</div>
                            <div className="text-[11px] text-text-muted mt-0.5">{meta.hint}</div>
                          </div>
                          <ChevronRight size={14} className="text-text-dim shrink-0 mt-1" />
                        </div>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            </section>
          ))}

          {/* Permits — the six types, raised in the Permits module (numbered, approved, tracked) */}
          <section>
            <div className="flex items-baseline justify-between mb-2">
              <h2 className="text-[11px] font-semibold uppercase tracking-wider2 text-text-muted">Permits</h2>
              <Link to="/permits" className="text-[11px] text-accent hover:underline">Open permits register →</Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {PERMIT_TYPES.map(p => {
                const Icon = p.icon;
                return (
                  <Link key={p.key} to={`/permits/issue/${p.key}`}>
                    <Card className="h-full">
                      <div className="p-4 flex gap-3 items-start h-full">
                        <div className="w-9 h-9 rounded-md bg-accent/12 flex items-center justify-center shrink-0">
                          <Icon size={16} className="text-accent" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[14px] text-text font-medium">{p.label} permit</div>
                          <div className="text-[11px] text-text-muted mt-0.5">{p.hint}</div>
                        </div>
                        <ChevronRight size={14} className="text-text-dim shrink-0 mt-1" />
                      </div>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </section>
        </div>
      )}

      {/* Recent submissions */}
      {recent.length > 0 && (
        <section>
          <h2 className="text-[11px] font-semibold uppercase tracking-wider2 text-text-muted mb-2">
            <Clock size={11} className="inline mr-1 -mt-0.5" /> Your recent submissions
          </h2>
          <Card>
            {(recent as any[]).map((r, i) => (
              <div key={r.submission_id} className={`px-4 py-3 flex items-center gap-3 ${i < recent.length - 1 ? "border-b border-line" : ""}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-text">{r.template?.display_name ?? r.template?.template_code}</div>
                  <div className="text-[11px] font-mono text-text-muted">
                    {r.submission_number} · {fmtDateTime(r.captured_at)}{r.project?.project_code ? ` · ${r.project.project_code}` : ""}
                  </div>
                </div>
                <Pill label={r.status} tone={r.status === "approved" ? "approved" : r.status === "rejected" ? "rejected" : "pending"} small />
              </div>
            ))}
          </Card>
        </section>
      )}
    </div>
  );
}
